/**
 * Centralized file size limits configuration
 *
 * This file defines all file size limits used across the platform.
 * Import these constants instead of hardcoding values to ensure consistency.
 */

// Base constants
export const BYTES_PER_KB = 1_024;
export const BYTES_PER_MB = 1_048_576;
export const BYTES_PER_GB = 1_073_741_824;

// File size limits by type
export const FILE_SIZE_LIMITS = {
  // Upload limits (non-video)
  UPLOAD: {
    MAX_SIZE_GB: 3, // 3GB for all non-video uploads
    MAX_SIZE_MB: 3_072, // 3072MB
    MAX_SIZE_BYTES: 3_221_225_472, // 3GB
    MULTIPART_THRESHOLD_MB: 5, // Use multipart upload for files > 5MB
    MULTIPART_THRESHOLD_BYTES: 5_242_880, // 5MB
  },

  // Processing limits (non-video)
  PROCESSING: {
    MAX_SIZE_GB: 3, // 3GB max for backend processing
    MAX_SIZE_BYTES: 3_221_225_472, // 3GB
    MAX_SIZE_MB: 3_072, // 3072MB
  },

  // Download limits (for external URLs)
  DOWNLOAD: {
    MAX_SIZE_MB: 20, // 20MB for external file downloads
    MAX_SIZE_BYTES: 20_971_520, // 20MB
  },

  // Image processing limits
  IMAGE: {
    MAX_SIZE_MB: 10, // 10MB for image processing
    MAX_SIZE_BYTES: 10_485_760, // 10MB
    MAX_DIMENSION: 6_000, // Max width/height in pixels
    MAX_PIXELS: 100_000_000, // Max total pixels (10000 * 10000)
  },

  // Video limits (uploads and processing)
  VIDEO: {
    MAX_SIZE_GB: 5, // 5GB for video uploads/processing
    MAX_SIZE_BYTES: 5_368_709_120, // 5GB
    MAX_DURATION_SECONDS: 7_200, // 2 hours
  },

  // Document limits
  DOCUMENT: {
    MAX_PAGES: 250,
    MAX_SIZE_MB: 3_072, // 3GB for documents
    MAX_SIZE_BYTES: 3_221_225_472, // 3GB
  },

  // Archive limits
  ARCHIVE: {
    MAX_FILES: 100,
    MAX_SIZE_MB: 3_072, // 3GB for archives
    MAX_SIZE_BYTES: 3_221_225_472, // 3GB
  },
} as const;

// Helper functions
export function formatFileSize(bytes: number): string {
  if (bytes >= BYTES_PER_GB) {
    const gb = bytes / BYTES_PER_GB;
    return `${gb % 1 === 0 ? gb.toFixed(0) : gb.toFixed(1)}GB`;
  }
  if (bytes >= BYTES_PER_MB) {
    const mb = bytes / BYTES_PER_MB;
    return `${mb % 1 === 0 ? mb.toFixed(0) : mb.toFixed(1)}MB`;
  }
  if (bytes >= BYTES_PER_KB) {
    const kb = bytes / BYTES_PER_KB;
    return `${kb % 1 === 0 ? kb.toFixed(0) : kb.toFixed(1)}KB`;
  }
  return `${bytes}B`;
}

export function validateFileSize(
  fileSize: number,
  limitType: keyof typeof FILE_SIZE_LIMITS,
  subType?: string,
): { isValid: boolean; limit: number; formattedLimit: string } {
  const limits = FILE_SIZE_LIMITS[limitType];

  // Determine which limit to use
  let limit: number;
  if (subType) {
    limit = limits[subType as keyof typeof limits] as number;
  } else {
    // Default to the most appropriate size limit for the type
    const limitsAny = limits as any;
    if ("MAX_SIZE_BYTES" in limits) {
      limit = limitsAny.MAX_SIZE_BYTES;
    } else if ("MAX_SIZE_MB" in limits) {
      limit = limitsAny.MAX_SIZE_MB * BYTES_PER_MB;
    } else if ("MAX_SIZE_GB" in limits) {
      limit = limitsAny.MAX_SIZE_GB * BYTES_PER_GB;
    } else {
      throw new Error(`No size limit found for ${limitType}`);
    }
  }

  return {
    isValid: fileSize <= limit,
    limit,
    formattedLimit: formatFileSize(limit),
  };
}

// Type exports for TypeScript
export type FileSizeLimitType = keyof typeof FILE_SIZE_LIMITS;
export type FileSizeLimitSubType<T extends FileSizeLimitType> =
  keyof (typeof FILE_SIZE_LIMITS)[T];
